import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Users, MessageCircle, Megaphone, Flag, Crosshair } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { usePagedEntity } from '../hooks/useEntity';
import { potensiEksternalApi, statsApi, desaApi } from '../lib/api';
import EntityFormDialog from '../components/EntityFormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DptSearchBox from '../components/DptSearchBox';
import { Pager, formatNumber } from '../components/shared/UI';
import { KECAMATAN_LIST } from '../mock/mockData';
import { useAuth } from '../context/AuthContext';

const KATEGORI_OPTIONS = [
  { value: 'tokoh_penggerak', label: 'Tokoh Penggerak Suara' },
  { value: 'timses_caleg_lain', label: 'Timses Caleg Partai Lain' },
  { value: 'caleg_lain', label: 'Caleg Partai Lain' },
];
const KATEGORI_LABEL = Object.fromEntries(KATEGORI_OPTIONS.map(o => [o.value, o.label]));
const KATEGORI_BADGE = {
  tokoh_penggerak: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  timses_caleg_lain: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  caleg_lain: 'bg-red-100 text-red-700 hover:bg-red-100',
};
const KATEGORI_ICON = { tokoh_penggerak: Megaphone, timses_caleg_lain: Flag, caleg_lain: Crosshair };

const LATAR_BELAKANG_PRESETS = ['Tokoh Masyarakat', 'Tokoh Agama', 'Pengurus DKM', 'Guru Ngaji', 'Ketua Poktan'];
const LAINNYA = '__lainnya__';
const LATAR_BELAKANG_OPTIONS = [...LATAR_BELAKANG_PRESETS.map(l => ({ value: l, label: l })), { value: LAINNYA, label: 'Lainnya (isi manual)' }];

const STATUS_OPTIONS = [
  { value: 'belum_didekati', label: 'Belum Didekati' },
  { value: 'sedang_didekati', label: 'Sedang Didekati' },
  { value: 'berhasil_digaet', label: 'Berhasil Digaet' },
  { value: 'tidak_bisa', label: 'Tidak Bisa Digaet' },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map(o => [o.value, o.label]));
const STATUS_BADGE = {
  belum_didekati: 'bg-gray-100 text-gray-600 hover:bg-gray-100',
  sedang_didekati: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  berhasil_digaet: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  tidak_bisa: 'bg-red-100 text-red-700 hover:bg-red-100',
};

const normalizeWa = (hp) => { if (!hp) return ''; let s = String(hp).replace(/\D/g, ''); if (s.startsWith('0')) s = '62' + s.slice(1); else if (!s.startsWith('62')) s = '62' + s; return s; };

const useDesaMap = () => {
  const [desaMap, setDesaMap] = useState({});
  useEffect(() => {
    desaApi.list().then(r => setDesaMap(Object.fromEntries(r.data.map(d => [d.kecamatan, d.desa])))).catch(() => {});
  }, []);
  return desaMap;
};

const resolveDesas = (kec, desaMap) => {
  if (!kec || !desaMap) return [];
  const direct = desaMap[kec];
  if (direct) return direct;
  const entry = Object.entries(desaMap).find(([k]) => k.toLowerCase() === String(kec).trim().toLowerCase());
  return entry ? entry[1] : [];
};

const buildFields = (desaMap) => [
  { type: 'section', label: 'Data Diri' },
  { name: 'nama', label: 'Nama Lengkap', required: true, placeholder: 'Nama tokoh/timses/caleg' },
  { name: 'kategori', label: 'Kategori', required: true, type: 'select', options: KATEGORI_OPTIONS },
  { name: 'partai_asal', label: 'Partai Asal', placeholder: 'Isi kalau kategori Timses/Caleg partai lain', hint: 'Hanya relevan untuk Timses Caleg Partai Lain / Caleg Partai Lain' },
  { name: 'latar_belakang_preset', label: 'Latar Belakang', type: 'select', options: LATAR_BELAKANG_OPTIONS, placeholder: 'Pilih latar belakang' },
  { name: 'latar_belakang', label: 'Latar Belakang (isi manual)', placeholder: 'mis. Ketua RT, Pemuka Adat, dll', showIf: { latar_belakang_preset: LAINNYA } },
  { name: 'hp', label: 'No. HP', placeholder: '0812xxxx' },

  { type: 'section', label: 'Wilayah' },
  { name: 'kecamatan', label: 'Kecamatan', required: true, type: 'select', options: KECAMATAN_LIST.map(k => ({ value: k.name, label: k.name })) },
  { name: 'desa', label: 'Desa / Kelurahan', type: 'select', placeholder: 'Pilih Kecamatan dulu',
    options: (data) => resolveDesas(data.kecamatan, desaMap).map(d => ({ value: d.nama, label: d.nama })) },
  { name: 'rw', label: 'RW', placeholder: 'RW 01' },
  { name: 'rt', label: 'RT', placeholder: 'RT 01' },

  { type: 'section', label: 'Pendekatan' },
  { name: 'status_pendekatan', label: 'Status Pendekatan', type: 'select', options: STATUS_OPTIONS },
  { name: 'pic', label: 'PIC yang Menangani', placeholder: 'Nama PIC' },
  { name: 'catatan', label: 'Catatan', full: true, placeholder: 'Catatan pendekatan, relasi, dll' },
];

const PotensiEksternal = () => {
  const { user } = useAuth();
  const canWrite = ['super_admin', 'admin_pusat', 'admin_input', 'koordinator', 'ketua_dpc'].includes(user?.role);
  const desaMap = useDesaMap();
  const FIELDS = buildFields(desaMap);
  const [search, setSearch] = useState('');
  const [filterKategori, setFilterKategori] = useState('all');
  const { items, total, page, pages, limit, loading, setPage, create, update, remove } = usePagedEntity(potensiEksternalApi, 'Potensi Eksternal', {
    search, filters: { kategori: filterKategori !== 'all' ? filterKategori : undefined },
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [dptPicked, setDptPicked] = useState(null);

  const [rekap, setRekap] = useState([]);
  const [rekapLoading, setRekapLoading] = useState(true);
  const [rekapFilterKec, setRekapFilterKec] = useState('all');
  const loadRekap = () => {
    setRekapLoading(true);
    statsApi.potensiEksternalDetail().then(r => setRekap(r.data)).catch(() => setRekap([])).finally(() => setRekapLoading(false));
  };
  useEffect(() => { loadRekap(); }, []);

  const totals = rekap.reduce((a, b) => ({
    tokoh_penggerak: a.tokoh_penggerak + (b.tokoh_penggerak || 0),
    timses_caleg_lain: a.timses_caleg_lain + (b.timses_caleg_lain || 0),
    caleg_lain: a.caleg_lain + (b.caleg_lain || 0),
    total: a.total + (b.total || 0),
  }), { tokoh_penggerak: 0, timses_caleg_lain: 0, caleg_lain: 0, total: 0 });

  const rekapKecList = [...new Set(rekap.map(r => r.kecamatan))].filter(Boolean).sort();
  const rekapFiltered = rekap.filter(r => rekapFilterKec === 'all' || r.kecamatan === rekapFilterKec);

  const handleDptPick = (dpt) => {
    setDptPicked(dpt);

    // 1. Cocokkan Kecamatan ke format resmi KECAMATAN_LIST (mis. 'BANTARGADUNG' -> 'Bantargadung')
    const rawKec = (dpt.kecamatan || '').trim().replace(/^(kecamatan|kec\.?)\s+/i, '');
    const matchedKec = KECAMATAN_LIST.find(k => k.name.toLowerCase() === rawKec.toLowerCase() || k.name.toLowerCase() === (dpt.kecamatan || '').trim().toLowerCase())?.name || dpt.kecamatan;

    // 2. Cocokkan Desa/Kelurahan ke format resmi desaMap
    const desas = resolveDesas(matchedKec, desaMap);
    const rawDesa = (dpt.desa || '').trim().replace(/^(desa|kelurahan|kel\.?)\s+/i, '');
    const matchedDesa = desas.find(d => {
      const dClean = d.nama.replace(/^(desa|kelurahan|kel\.?)\s+/i, '').trim().toLowerCase();
      return dClean === rawDesa.toLowerCase() || d.nama.toLowerCase() === (dpt.desa || '').trim().toLowerCase();
    })?.nama || dpt.desa;

    // 3. Rapikan TPS
    const rawTps = (dpt.tps || '').trim();
    const cleanTps = rawTps.replace(/^tps\s*/i, '');

    setEditData(prev => ({
      ...prev,
      nama: dpt.nama,
      kecamatan: matchedKec,
      desa: matchedDesa || '',
      rw: dpt.rw || '',
      rt: dpt.rt || '',
      tps: cleanTps ? `TPS ${cleanTps}` : rawTps,
      dpt_id: dpt.id,
    }));
  };

  const handleDptClear = () => {
    setDptPicked(null);
  };

  const openAdd = () => {
    setDptPicked(null);
    setEditData({ kategori: 'tokoh_penggerak', status_pendekatan: 'belum_didekati' });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setDptPicked(null);
    const isPreset = LATAR_BELAKANG_PRESETS.includes(p.latar_belakang);
    setEditData({
      ...p,
      latar_belakang_preset: isPreset ? p.latar_belakang : (p.latar_belakang ? LAINNYA : ''),
      latar_belakang: isPreset ? '' : (p.latar_belakang || ''),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (data) => {
    const { latar_belakang_preset, ...rest } = data;
    const payload = {
      ...rest,
      latar_belakang: latar_belakang_preset && latar_belakang_preset !== LAINNYA ? latar_belakang_preset : (data.latar_belakang || ''),
    };
    const ok = editData?.id ? await update(editData.id, payload) : await create(payload);
    if (ok) loadRekap();
    return ok;
  };

  const handleRemove = async (id) => {
    const ok = await remove(id);
    if (ok) loadRekap();
  };

  const stats = [
    { l: 'Total Potensi Eksternal', v: totals.total, c: 'text-orange-600', bg: 'bg-orange-50', icon: Users },
    { l: 'Tokoh Penggerak Suara', v: totals.tokoh_penggerak, c: 'text-emerald-600', bg: 'bg-emerald-50', icon: Megaphone },
    { l: 'Timses Caleg Partai Lain', v: totals.timses_caleg_lain, c: 'text-amber-600', bg: 'bg-amber-50', icon: Flag },
    { l: 'Caleg Partai Lain', v: totals.caleg_lain, c: 'text-red-600', bg: 'bg-red-50', icon: Crosshair },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4 text-sm">
        <p className="font-bold text-orange-900">Data Potensi Eksternal</p>
        <p className="text-xs text-orange-800 font-medium mt-1">
          Tokoh/timses/caleg di luar jaringan internal kita yang berpotensi menggiring suara.
          Data ini <b>tidak ikut dihitung</b> di Realisasi/Target Suara — hanya untuk pemetaan pendekatan.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {stats.map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-5 card-shadow card-hover">
            <div className={`w-11 h-11 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
              <s.icon className={`w-5 h-5 ${s.c}`} />
            </div>
            <p className="text-sm font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{formatNumber(s.v)}</h3>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-[300px]">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama, No. HP, atau kecamatan..." className="pl-9 h-10" />
            </div>
            <Select value={filterKategori} onValueChange={setFilterKategori}>
              <SelectTrigger className="w-56 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kategori</SelectItem>
                {KATEGORI_OPTIONS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {canWrite && (
            <Button onClick={openAdd} className="h-10 bg-orange-500 hover:bg-orange-600 gap-2 font-bold"><Plus className="w-4 h-4" /> Tambah</Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">Nama</TableHead>
                <TableHead className="font-extrabold">Kategori</TableHead>
                <TableHead className="font-extrabold">Latar Belakang</TableHead>
                <TableHead className="font-extrabold">Kecamatan / Desa</TableHead>
                <TableHead className="font-extrabold">Status Pendekatan</TableHead>
                <TableHead className="font-extrabold">PIC</TableHead>
                <TableHead className="font-extrabold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400 font-semibold">Memuat data...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400 font-semibold">Belum ada data. Klik "Tambah" untuk mulai.</TableCell></TableRow>
              ) : items.map(p => {
                const Icon = KATEGORI_ICON[p.kategori] || Megaphone;
                return (
                  <TableRow key={p.id} className="hover:bg-orange-50/30">
                    <TableCell className="font-bold text-gray-900">{p.nama}</TableCell>
                    <TableCell><Badge className={`gap-1 ${KATEGORI_BADGE[p.kategori]}`}><Icon className="w-3 h-3" /> {KATEGORI_LABEL[p.kategori] || p.kategori}</Badge></TableCell>
                    <TableCell className="text-xs font-semibold text-gray-600">{p.latar_belakang || '-'}{p.partai_asal && ` · ${p.partai_asal}`}</TableCell>
                    <TableCell className="text-xs font-semibold text-gray-600">{p.kecamatan}{p.desa && `, ${p.desa}`}</TableCell>
                    <TableCell><Badge className={STATUS_BADGE[p.status_pendekatan]}>{STATUS_LABEL[p.status_pendekatan] || p.status_pendekatan}</Badge></TableCell>
                    <TableCell className="text-xs font-semibold text-gray-600">{p.pic || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {p.hp && <a href={`https://wa.me/${normalizeWa(p.hp)}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-500 hover:text-emerald-600"><MessageCircle className="w-4 h-4" /></a>}
                        {canWrite && <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-600"><Edit className="w-4 h-4" /></button>}
                        {canWrite && <button onClick={() => setConfirmDel(p)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pager page={page} pages={pages} total={total} limit={limit} onPageChange={setPage} />
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h3 className="text-xl font-extrabold">Rekap per Wilayah</h3>
            <p className="text-sm text-gray-500 font-medium">Jumlah Potensi Eksternal per Kecamatan, Desa, RW, sampai RT</p>
          </div>
          <Select value={rekapFilterKec} onValueChange={setRekapFilterKec}>
            <SelectTrigger className="w-56 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kecamatan</SelectItem>
              {rekapKecList.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">Kecamatan</TableHead>
                <TableHead className="font-extrabold">Desa</TableHead>
                <TableHead className="font-extrabold">RW</TableHead>
                <TableHead className="font-extrabold">RT</TableHead>
                <TableHead className="font-extrabold text-right">Tokoh Penggerak</TableHead>
                <TableHead className="font-extrabold text-right">Timses Caleg Lain</TableHead>
                <TableHead className="font-extrabold text-right">Caleg Lain</TableHead>
                <TableHead className="font-extrabold text-right bg-orange-100">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rekapLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400 font-semibold">Memuat...</TableCell></TableRow>
              ) : rekapFiltered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400 font-semibold">Belum ada data Potensi Eksternal tercatat.</TableCell></TableRow>
              ) : rekapFiltered.map((r, i) => (
                <TableRow key={i} className="hover:bg-orange-50/30">
                  <TableCell className="font-bold whitespace-nowrap">{r.kecamatan}</TableCell>
                  <TableCell className="font-semibold text-gray-600">{r.desa || '-'}</TableCell>
                  <TableCell className="font-semibold text-gray-600">{r.rw || '-'}</TableCell>
                  <TableCell className="font-semibold text-gray-600">{r.rt || '-'}</TableCell>
                  <TableCell className="text-right font-medium text-emerald-600">{r.tokoh_penggerak}</TableCell>
                  <TableCell className="text-right font-medium text-amber-600">{r.timses_caleg_lain}</TableCell>
                  <TableCell className="text-right font-medium text-red-600">{r.caleg_lain}</TableCell>
                  <TableCell className="text-right font-extrabold text-orange-600 bg-orange-50/30">{r.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <EntityFormDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        title={editData?.id ? `Edit ${editData.nama}` : 'Tambah Potensi Eksternal Baru'}
        description="Catat tokoh/timses/caleg partai lain yang berpotensi menggiring suara."
        fields={FIELDS} initialData={editData || {}} onSubmit={handleSubmit}
        extraHeader={!editData?.id && <DptSearchBox picked={dptPicked} onPick={handleDptPick} onClear={handleDptClear} />}
      />
      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Hapus Potensi Eksternal?" description={`Data "${confirmDel?.nama}" akan dihapus permanen.`}
        onConfirm={() => handleRemove(confirmDel.id)}
      />
    </div>
  );
};

export default PotensiEksternal;
